import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import { MoldService } from '../../service/api/mold-service';
import { showToast } from 'Core/redux/toast/slice';

const FutureActionsDetailsModal = ({ showModal, onCloseModal, modalData }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [futureActionsData, setFutureActionsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(new Set());
  const focusedFutureActionId = modalData?.focusedFutureActionId;

  console.log('🔍 FutureActionsDetailsModal - showModal:', showModal);
  console.log('🔍 FutureActionsDetailsModal - modalData:', modalData);

  useEffect(() => {
    if (showModal && modalData?.moldInfoID) {
      console.log('🔍 Loading future actions for moldInfoID:', modalData.moldInfoID);
      loadFutureActions(modalData.moldInfoID);
    }
  }, [showModal, modalData]);

  const loadFutureActions = async (moldInfoId) => {
    try {
      setLoading(true);
      console.log('Loading future actions for moldInfoId:', moldInfoId);
      const response = await MoldService.getMoldMaintenanceFutureActions(moldInfoId);
      console.log('Future Actions API response:', response);
      
      if (response.status === 200) {
        setFutureActionsData(response.response || []);
      } else {
        console.error('Failed to load future actions:', response);
        dispatch(showToast({
          message: 'Failed to load future actions',
          type: 'danger'
        }));
      }
    } catch (error) {
      console.error('Error loading future actions:', error);
      dispatch(showToast({
        message: 'Error loading future actions: ' + error.message,
        type: 'danger'
      }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (loading || !focusedFutureActionId || !futureActionsData.length) {
      return undefined;
    }
    const frame = requestAnimationFrame(() => {
      document
        .getElementById(`future-action-${focusedFutureActionId}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
    return () => cancelAnimationFrame(frame);
  }, [loading, focusedFutureActionId, futureActionsData]);

  const handleClose = () => {
    onCloseModal();
    const params = new URLSearchParams(location.search);
    if (params.has('moldInfoId') || params.has('futureActionId') || params.has('maintenanceId')) {
      navigate('/future-actions', { replace: true });
    }
  };

  const handleStatusChange = async (actionIndex, newStatus) => {
    const actionToUpdate = futureActionsData[actionIndex];
    
    if (!actionToUpdate) {
      console.error('Action not found at index:', actionIndex);
      return;
    }

    // Update local state immediately for better UX
    setFutureActionsData(prevData => 
      prevData.map((action, index) => 
        index === actionIndex ? { ...action, status: newStatus } : action
      )
    );

    try {
      console.log('Saving status change for action:', actionToUpdate);
      console.log('New status:', newStatus);
      
      // Prepare payload for the API call
      const payload = {
        MoldInfoID: modalData?.moldInfoID || modalData?.id || 1,
        FutureAction: actionToUpdate.futureAction,
        Status: newStatus,
        MaintenanceId: actionToUpdate.maintenanceId || 0,
        FutureActionId: actionToUpdate.futureActionId || actionToUpdate.id
      };

      console.log('API payload:', payload);

      const response = await MoldService.saveMoldMaintenanceFutureAction(payload);
      console.log('Save API response:', response);

      if (response.status === 200) {
        dispatch(showToast({
          message: 'Status updated successfully!',
          type: 'success'
        }));
        console.log('Status updated successfully');
        
        // Refresh the future actions data after successful update
        const moldInfoId = modalData?.moldInfoID || modalData?.id || 1;
        console.log('Refreshing future actions data with moldInfoId:', moldInfoId);
        
        try {
          const refreshResponse = await MoldService.getMoldMaintenanceFutureActions(moldInfoId);
          console.log('Refresh API response:', refreshResponse);
          
          if (refreshResponse.status === 200) {
            setFutureActionsData(refreshResponse.response || []);
            console.log('Future actions data refreshed successfully');
          } else {
            console.warn('Failed to refresh future actions data:', refreshResponse);
          }
        } catch (refreshError) {
          console.error('Error refreshing future actions data:', refreshError);
        }
      } else {
        console.error('Failed to update status:', response);
        dispatch(showToast({
          message: 'Failed to update status. Please try again.',
          type: 'danger'
        }));
        
        // Revert the local state change if API failed
        setFutureActionsData(prevData => 
          prevData.map((action, index) => 
            index === actionIndex ? { ...action, status: actionToUpdate.status } : action
          )
        );
      }
    } catch (error) {
      console.error('Error updating status:', error);
      dispatch(showToast({
        message: 'Error updating status: ' + (error.message || 'Unknown error'),
        type: 'danger'
      }));
      
      // Revert the local state change if API failed
      setFutureActionsData(prevData => 
        prevData.map((action, index) => 
          index === actionIndex ? { ...action, status: actionToUpdate.status } : action
        )
      );
    }
  };

  return (
    <Modal
      show={showModal}
      onHide={handleClose}
      size="lg"
      centered
      className="future-actions-modal"
    >
      <Modal.Header 
        closeButton 
        className="text-white border-0"
        style={{ 
          backgroundColor: '#1a365d',
          borderRadius: '8px 8px 0 0',
          color: 'white'
        }}
      >
        <Modal.Title className="fw-bold fs-5" style={{ color: 'white' }}>
          <i className="fas fa-tasks me-2"></i>
          Future Actions - {modalData?.toolID || 'Tool Details'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-4" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="row">
          <div className="col-12">
            {/* Tool Information Card */}
            <div className="card mb-4 border-0 shadow-sm">
              <div className="card-header bg-white border-bottom">
                <h6 className="fw-bold mb-0 text-primary">
                  <i className="fas fa-info-circle me-2"></i>
                  Tool Information
                </h6>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <div className="d-flex align-items-center">
                      <div className="me-3">
                        <i className="fas fa-tag text-primary fs-5"></i>
                      </div>
                      <div>
                        <small className="text-muted d-block">Tool ID</small>
                        <strong className="fs-6">{modalData?.toolID || 'N/A'}</strong>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <div className="d-flex align-items-center">
                      <div className="me-3">
                        <i className="fas fa-file-alt text-primary fs-5"></i>
                      </div>
                      <div>
                        <small className="text-muted d-block">Description</small>
                        <strong className="fs-6">{modalData?.moldDescription || modalData?.description || 'N/A'}</strong>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Future Actions Card */}
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white border-bottom">
                <h6 className="fw-bold mb-0 text-primary">
                  <i className="fas fa-list-check me-2"></i>
                  Future Actions
                </h6>
              </div>
              <div className="card-body p-0">
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="text-muted fs-6">Loading future actions...</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover mb-0" style={{ borderCollapse: 'separate', borderSpacing: '0' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f8f9fa' }}>
                          <th className="fw-bold text-dark py-4 px-4" style={{ 
                            borderBottom: '2px solid #dee2e6',
                            fontSize: '14px',
                            letterSpacing: '0.5px'
                          }}>
                            <i className="fas fa-tasks me-2 text-primary"></i>
                            Future Actions
                          </th>
                          <th className="fw-bold text-dark py-4 px-4" style={{ 
                            borderBottom: '2px solid #dee2e6',
                            fontSize: '14px',
                            letterSpacing: '0.5px'
                          }}>
                            <i className="fas fa-flag me-2 text-primary"></i>
                            Status
                          </th>
                          <th className="fw-bold text-dark py-4 px-4" style={{ 
                            borderBottom: '2px solid #dee2e6',
                            fontSize: '14px',
                            letterSpacing: '0.5px'
                          }}>
                            <i className="fas fa-calendar me-2 text-primary"></i>
                            Noted Date
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {futureActionsData && futureActionsData.length > 0 ? (
                          futureActionsData.map((action, actionIndex) => {
                            const actionId = action.futureActionId || action.id;
                            const isFocused =
                              focusedFutureActionId != null &&
                              String(actionId) === String(focusedFutureActionId);
                            return (
                            <tr 
                              key={`api-${actionId || actionIndex}`}
                              id={actionId != null ? `future-action-${actionId}` : undefined}
                              style={{ 
                                borderBottom: '1px solid #e9ecef',
                                transition: 'all 0.2s ease',
                                backgroundColor: isFocused ? '#e3f2fd' : 'transparent',
                                boxShadow: isFocused ? 'inset 3px 0 0 #1976d2' : undefined,
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = isFocused ? '#d0e7fa' : '#f8f9fa';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = isFocused ? '#e3f2fd' : 'transparent';
                              }}
                            >
                              <td className="py-4 px-4" style={{ borderRight: '1px solid #e9ecef' }}>
                                <div className="d-flex align-items-center">
                                  <div className="me-3">
                                    <i className="fas fa-circle text-primary" style={{ fontSize: '8px' }}></i>
                                  </div>
                                  <span className="fw-medium text-dark" style={{ fontSize: '14px' }}>
                                    {action.futureAction}
                                  </span>
                                </div>
                              </td>
                              <td className="py-4 px-4" style={{ borderRight: '1px solid #e9ecef' }}>
                                <select
                                  className="form-select form-select-sm"
                                  value={action.status}
                                  onChange={(e) => handleStatusChange(actionIndex, e.target.value)}
                                  style={{ 
                                    minWidth: '140px',
                                    border: '2px solid transparent',
                                    borderRadius: '6px',
                                    fontSize: '13px',
                                    fontWeight: '500',
                                    backgroundColor: action.status === 'Open' ? '#fff3cd' : 
                                                   action.status === 'Fixed' ? '#d1edff' : '#f8d7da',
                                    transition: 'all 0.3s ease',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                  }}
                                  onFocus={(e) => {
                                    e.target.style.borderColor = '#007bff';
                                    e.target.style.boxShadow = '0 0 0 0.2rem rgba(0,123,255,0.25)';
                                  }}
                                  onBlur={(e) => {
                                    e.target.style.borderColor = 'transparent';
                                    e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                                  }}
                                >
                                  <option value="Open">🟡 Open</option>
                                  <option value="Fixed">🟢 Fixed</option>
                                  <option value="On Hold">🔴 On Hold</option>
                                </select>
                              </td>
                              <td className="py-4 px-4">
                                <div className="d-flex align-items-center">
                                  <i className="fas fa-calendar-alt me-2 text-muted" style={{ fontSize: '12px' }}></i>
                                  <span className="text-muted fw-medium" style={{ fontSize: '13px' }}>
                                    {action.notedDate ? new Date(action.notedDate).toISOString().split('T')[0] : 'N/A'}
                                  </span>
                                </div>
                              </td>
                            </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan="3" className="text-center py-5" style={{ backgroundColor: '#f8f9fa' }}>
                              <div className="d-flex flex-column align-items-center">
                                <i className="fas fa-inbox text-muted mb-3" style={{ fontSize: '3rem' }}></i>
                                <h6 className="text-muted mb-2">No Future Actions</h6>
                                <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
                                  No future actions have been recorded yet.
                                </p>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="bg-light border-0" style={{ borderRadius: '0 0 8px 8px' }}>
        <button
          type="button"
          className="btn btn-primary px-4 py-2 fw-semibold"
          onClick={handleClose}
          style={{ 
            borderRadius: '6px',
            boxShadow: '0 2px 4px rgba(0,123,255,0.3)',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-1px)';
            e.target.style.boxShadow = '0 4px 8px rgba(0,123,255,0.4)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 2px 4px rgba(0,123,255,0.3)';
          }}
        >
          <i className="fas fa-times me-2"></i>
          Close
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default FutureActionsDetailsModal;
